import { ArrowBack, CheckCircle, Dehaze, FilterList } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Card, CardContent, Container, Divider, Grid, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BrandEnum } from '../../_common/enum/brand.enum';
import { FormTemplateTypeEnum } from '../../_common/enum/forms/formTemplateType.enum';
import { QuoteLevelEnum } from '../../_common/enum/quoteLevel.enum';
import { QuoteTypeEnum } from '../../_common/enum/quoteType.enum';
import { ScreenSizeCategoryEnum } from '../../_common/enum/screenSizeCategory.enum';
import { IQuoteProviderComparisonResults } from '../../_common/interface/quoteProviderComparison/iQuoteProviderComparisonResults.interface';
import { IQuoteProviderComparisonResultsGrouped } from '../../_common/interface/quoteProviderComparison/iQuoteProviderComparisonResultsGrouped.interface';
import { LoaderDoubleLines } from '../../_components/loader/LoaderDoubleLines';
import { quoteTypeOrder } from '../../_const/quoteTypeDisplayOrder.const';
import { getBrandCallbackLink } from '../../_helpers/brand.helper';
import { getMoneyFormatNoFractions, replaceSpaceAndGetEnumValue, splitWordOnCamelCase } from '../../_helpers/format.helper';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { retryHeight, sendBodyHeightToParent, sendContainerHeightToParent, sendScrollMessageToParent } from '../../_helpers/iFrameSize.helper';
import { formatQuoteDescription, formatQuotePrice, getAgeAndMonths } from '../../_helpers/lowQuote.format.helper';
import { getWindowSizeCategory, useScreenSize } from '../../_helpers/screenSize.helper';
import { formService } from '../../_services/form.service';
import { withFormAccessToken } from '../../_helpers/formAccessToken.helper';
import { quoteComparisonService } from '../../_services/quoteComparison.service';
import { useBoxStyles } from '../../_styles/box.styles';
import { QuotesNoResults } from './quotes-noResults';
import { QuoteSection } from './quoteSection';
import { QuoteFilters } from './quotesFilters';

export function Quotes(params: { brand: BrandEnum }) {

    const resetData = () => ({
        regularQuotes: [],
        goldQuotes: [],
        bronzeQuotes: [],
        silverQuotes: [],
        quoteId: 0,
        clientId: 0,
        gender: '',
        genderJointLife: null,
        dateOfBirth: '',
        dateOfBirthJointLife: null,
        smokingStatus: '',
        smokingStatusJointLife: null,
        coverAmount: 0,
        coverAmountJointLife: null,
        term: 0,
        salary: null,
        employmentType: null,
        quoteType: 0,
        isJointLife: false,
        quoteRef: "",
        firstName: "",
        firstNameJointLife: null,
        occupation: null
    });
    const location = useLocation();

    const { formId } = useParams();
    const { productName } = useParams();
    const [data, setData] = useState<IQuoteProviderComparisonResultsGrouped>(resetData);
    const [healthApplicationData, setHealthApplicationData] = useState<any>({});
    const [screenSizeCategory, setScreenSizeCategory] = useState<ScreenSizeCategoryEnum | null>();
    const [isLoading, setLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [quoteType, setQuoteType] = useState<string | null>("");
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(true);
    const [expandedProviderId, setExpandedProviderId] = useState<number | null>(null);
    const [taxRelief, setTaxRelief] = useState<number | null>(null);
    const [employmentType, setEmploymentType] = useState<string | null>(null);

    const getRank = (q: any) => {
        const r = q.CheapestPriceOrderNo ?? q.cheapestPriceOrderNo;
        return typeof r === "number" ? r : Number.MAX_SAFE_INTEGER;
    };

    const sortedRegularQuotesByPrice = [...data.regularQuotes]
        .filter(q => q.providerQuote > 0)
        .sort((a, b) => {
            const priceDiff = a.providerQuote - b.providerQuote;
            if (priceDiff !== 0) return priceDiff;                // primary: cheaper first

            const rankDiff = getRank(a) - getRank(b);
            if (rankDiff !== 0) return rankDiff;                  // tie-breaker: rank

            return (a.providerName || "").localeCompare(b.providerName || ""); // final stable tie-break
        });

    const findQuotesByProvider = (providerId: number) => ({
        bronze: data.bronzeQuotes.find((quote: IQuoteProviderComparisonResults) => quote.providerId === providerId),
        silver: data.silverQuotes.find((quote: IQuoteProviderComparisonResults) => quote.providerId === providerId),
        gold: data.goldQuotes.find((quote: IQuoteProviderComparisonResults) => quote.providerId === providerId),
    });
    const screenSize = useScreenSize();

    useEffect(() => {
        sendScrollMessageToParent();
    }, []);

    useEffect(() => {
        setScreenSizeCategory(getWindowSizeCategory(screenSize.width));
    }, [screenSize]);

    useEffect(() => {
        fetchQuotes();
    }, [formId]);

    useEffect(() => {
        if (isSaved) {
            fetchQuotes();
        }
    }, [isSaved]);

    useEffect(() => {
        if (!isLoading) {
            retryHeight(sendBodyHeightToParent);
        }
    }, [isLoading]);

    function fetchQuotes() {
        if (!formId) return;

        setLoading(true);
        return quoteComparisonService
            .getQuotes(Number(formId), params.brand)
            .then((_data) => {
                setData(_data);

                if (_data.salary != null) {
                    setTaxRelief(_data.salary < 44000 ? 20 : 40);
                }

                if (_data.employmentType) {
                    setEmploymentType(_data.employmentType);
                }

                setQuoteType(
                    _data.quoteType === QuoteTypeEnum.LifeInsurance
                        ? "Level Term Life "
                        : _data.quoteType === QuoteTypeEnum.MortgageProtection
                            ? "Mortgage Protection"
                            : _data.quoteType === QuoteTypeEnum.IncomeProtection
                                ? "Level Term Income "
                                : _data.quoteType === QuoteTypeEnum.ExecutiveIncomeProtection
                                    ? "Level Term Executive Income "
                                    : _data.quoteType === QuoteTypeEnum.IllnessCover
                                        ? "Level Term Serious Illness "
                                        : " "
                );

                const healthAppDataVm = {
                    clientId: _data.clientId,
                    quoteId: _data.quoteId,
                    isJointLife: _data.isJointLife,
                    firstName: _data.firstName,
                    firstNameJoint: _data.firstNameJointLife,
                    lastName: _data.lastName,
                    lastNameJointLife: _data.lastNameJointLife,
                    smokingStatus: _data.smokingStatus,
                    smokingStatusJoint: _data.smokingStatusJointLife,
                    dob: _data.dateOfBirth,
                    dobJoint: _data.dateOfBirthJointLife,
                    phone: _data.phoneNumber,
                    email: _data.emailAddress,
                    quoteRef: _data.quoteRef,
                    salary: _data.salary
                };

                setHealthApplicationData(healthAppDataVm);
            })
            .catch(handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function toggleExpandCards(projectId: number) {
        if (expandedProviderId === projectId) {
            setExpandedProviderId(null);
        } else {
            setExpandedProviderId(projectId);

            setTimeout(() => {
                setExpandedProviderId(null);
            }, 600);
        };
    }

    function switchForms(formTemplateTypeId: FormTemplateTypeEnum) {
        if (data && formTemplateTypeId !== replaceSpaceAndGetEnumValue(productName!, FormTemplateTypeEnum)) {
            return formService.createByType(formTemplateTypeId, params.brand)
                .then(
                    ({ id, token }) => {
                        navigate(withFormAccessToken(`/forms/${id}`, id, token));
                    }, handleErrorResponse)
                .finally(() => {
                    setLoading(false);
                    retryHeight(sendContainerHeightToParent);
                });
        }
        else if (data) {
            navigate(withFormAccessToken(`/forms/${formId}`, Number(formId)));
            retryHeight(sendContainerHeightToParent);
        }
        else {
            return;
        }
    }

    function createNewFormForClient(formTemplateTypeId: FormTemplateTypeEnum, providerId: number, quoteType?: QuoteTypeEnum) {
        var _data = { formTemplateTypeId, quoteId: data.quoteId, clientId: data.clientId, providerId, quoteType, quoteLevel: QuoteLevelEnum.Regular }
        return formService.createByTypeForClient(_data)
            .then(
                ({ id, token }) => {
                    navigate(withFormAccessToken(`/formOriginal/${id}/${data.quoteId}`, id, token));
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    };
    const LowestQuoteCircle = () => {
        return (
            <Box
                sx={(theme) => ({
                    position: "relative",
                    width: { xs: 64, sm: 72, md: 112 },  
                    height: { xs: 64, sm: 72, md: 112 },
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: { xs: 1, md: 2.5 },          
                    boxSizing: "border-box",
                })}
            >
                <Typography
                    color="#fff"
                    fontWeight="bold"
                    sx={{
                        fontSize: { xs: "12px", sm: "14px", md: "20px" },
                        lineHeight: 1.15,
                    }}
                >
                    LOWEST
                    <br />
                    QUOTE
                </Typography>

                <CheckCircle
                    sx={(theme) => ({
                        position: "absolute",
                        bottom: { xs: -10, md: -18 },     
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: { xs: 22, md: 32 },
                        color: theme.palette.secondary.main,
                        backgroundColor: "#fff",
                        borderRadius: "50%",
                    })}
                />
            </Box>
        );
    };


    return (
        <>

            {isLoading ?
                <LoaderDoubleLines />
                :
                sortedRegularQuotesByPrice.length > 0 ?
                    <>
                        <Container maxWidth="md" style={{ margin: "0px auto", padding: "2px" }}>


                            <Box display="flex" flexDirection="column" gap={1} margin={1}>

                                <ButtonGroup variant="outlined">
                                    {quoteTypeOrder
                                        .map((numericValue) => {

                                            return (
                                                <Button
                                                    key={numericValue}
                                                    onClick={() => switchForms(numericValue)}
                                                    sx={{
                                                        backgroundColor: !!productName && replaceSpaceAndGetEnumValue(productName, FormTemplateTypeEnum) === numericValue ? "primary.main" : "transparent",
                                                        color: !!productName && replaceSpaceAndGetEnumValue(productName, FormTemplateTypeEnum) === numericValue ? "white" : "primary.main",
                                                        "&:hover": {
                                                            backgroundColor: !!productName && replaceSpaceAndGetEnumValue(productName, FormTemplateTypeEnum) === numericValue ? "primary.dark" : "rgba(0, 0, 0, 0.08)",
                                                        },
                                                    }}
                                                >
                                                    {splitWordOnCamelCase(FormTemplateTypeEnum[numericValue])}
                                                </Button>
                                            );
                                        })}
                                </ButtonGroup>

                                <Typography variant="h6" fontWeight="bold">Your Quote Details</Typography>
                                <Typography variant="h6" color="primary">{quoteType} Insurance</Typography>

                                <Box display="flex" gap={1} width="100%">
                                    <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.firstName}, {data.gender}</Typography></Box>
                                    <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.smokingStatus}</Typography></Box>
                                    <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{getAgeAndMonths(data.dateOfBirth)}</Typography></Box>
                                </Box>
                                {(data.genderJointLife && data.dateOfBirthJointLife && data.smokingStatusJointLife) &&
                                    <Box display="flex" gap={1}>
                                        <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.firstNameJointLife}, {data.genderJointLife}</Typography></Box>
                                        <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.smokingStatusJointLife}</Typography></Box>
                                        <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{getAgeAndMonths(data.dateOfBirthJointLife)}</Typography></Box>
                                    </Box>
                                }
                                <Typography variant="subtitle2">Cover Details:</Typography>
                                <Box display="flex" gap={1}>
                                    <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.firstName} : {getMoneyFormatNoFractions(data.coverAmount)} {quoteType} Cover</Typography></Box>
                                    {(!!data.coverAmountJointLife || (!data.coverAmountJointLife && data.quoteType == QuoteTypeEnum.MortgageProtection && !!data.firstNameJointLife)) &&
                                        <Box sx={useBoxStyles.purpleBox}>
                                            <Typography variant="subtitle2">
                                                {data.firstNameJointLife} :
                                                {!!data.coverAmountJointLife ? getMoneyFormatNoFractions(data.coverAmountJointLife) : getMoneyFormatNoFractions(data.coverAmount)} {quoteType} Cover
                                            </Typography>
                                        </Box>
                                    }
                                    <Box sx={useBoxStyles.purpleBox}><Typography variant="subtitle2">{data.term} Year Term</Typography></Box>
                                </Box>
                            </Box>
                            {showFilters ?
                                <Box display="flex" justifyContent="space-between">
                                    <Box width="100%" m={1}>
                                        <Button
                                            startIcon={<ArrowBack />}
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                navigate(withFormAccessToken(`/forms/${formId}`, Number(formId)), { state: { stateCounter: (location.state?.stateCounter + 1) || 1 } });
                                            }}
                                            sx={{ width: "80% !important" }}
                                        >
                                            Change Quote Details
                                        </Button>
                                    </Box>
                                    <IconButton title="Hide Filters" onClick={() => setShowFilters(false)}>
                                        <Dehaze color="secondary" />
                                    </IconButton>
                                </Box>
                                :
                                <Box display="flex" justifyContent="space-between">
                                    <Box width="100%" m={1}>
                                        <Button
                                            startIcon={<ArrowBack />}
                                            variant="contained"
                                            onClick={() => {
                                                navigate(withFormAccessToken(`/forms/${formId}`, Number(formId)));
                                            }}
                                            sx={{ width: "80% !important", backgroundColor: "secondary" }}>
                                            Change Quote Details
                                        </Button>
                                    </Box>
                                    <IconButton title="Show Filters" onClick={() => setShowFilters(true)}>
                                        <FilterList color="secondary" />
                                    </IconButton>
                                </Box>
                            }
                        </Container>
                        {showFilters &&
                            <Grid item xs={12}>
                                <QuoteFilters setIsSaved={fetchQuotes} firstName={data.firstName} firstNameJointLife={data.firstNameJointLife} />
                            </Grid>
                        }
                    </>
                    : <QuotesNoResults />

            }


            {sortedRegularQuotesByPrice.map((regularQuote, index) => {
                const { bronze, silver, gold } = findQuotesByProvider(regularQuote.providerId);

                const quoteDescription = formatQuoteDescription(regularQuote.providerQuoteDescription, true, (regularQuote.quoteType === QuoteTypeEnum.IncomeProtection && !!employmentType) ? employmentType : null);

                return (
                    <>
                        <Container maxWidth="md" style={{ margin: "0px auto", padding: "2px" }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >

                                            <Card sx={{ backgroundColor: "#f2f2f2", flex: 1, padding: '0px !important' }}>
                                                <Box sx={useBoxStyles.cardHeaderBox}>
                                                    <Box sx={useBoxStyles.titleBox} bgcolor="primary.main">
                                                        <Typography variant="h5" textAlign="center" fontWeight="600">
                                                            YOUR QUOTE
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <CardContent sx={{
                                                    padding: { xs: '4px !important', md: '16px !important' }
                                                }}>
                                                    {/*<Box sx={useBoxStyles.logoBox}*/}
                                                    {/*    height={(screenSizeCategory === ScreenSizeCategoryEnum.XS || screenSizeCategory === ScreenSizeCategoryEnum.SM || screenSizeCategory === ScreenSizeCategoryEnum.MD) ? "100px" : "200px"}>*/}
                                                    <Box
                                                        sx={useBoxStyles.logoBox}
                                                        minHeight={
                                                            screenSizeCategory === ScreenSizeCategoryEnum.XS ||
                                                                screenSizeCategory === ScreenSizeCategoryEnum.SM
                                                                ? "80px"
                                                                : screenSizeCategory === ScreenSizeCategoryEnum.MD
                                                                    ? "120px"
                                                                    : "200px"
                                                        }
                                                    >
                                                        {index === 0 &&
                                                            <LowestQuoteCircle />
                                                        }
                                                        <img id="logoImg" style={{
                                                            width: "50%",
                                                            marginTop: "10px",
                                                            display: 'block',
                                                            marginLeft: 'auto',
                                                            marginRight: 'auto',
                                                            marginBottom: "10px"
                                                        }} src={regularQuote.providerAzureLogoUrl ? regularQuote.providerAzureLogoUrl : ""} alt={regularQuote.providerName}>
                                                        </img>
                                                    </Box>
                                                    <Box display="flex" width="100%" flexDirection={(data.quoteType === QuoteTypeEnum.IncomeProtection || data.quoteType === QuoteTypeEnum.ExecutiveIncomeProtection) ? "column" : "row"}>
                                                        <Box display="flex" flexGrow={1} flexDirection="column">
                                                            <Typography variant="h2" fontWeight="bold" textAlign="center">
                                                                {formatQuotePrice(regularQuote.providerQuote, false)}
                                                            </Typography>
                                                            <Typography variant="body1" textAlign="center" color="grey">
                                                                Monthly Premium
                                                            </Typography>
                                                            {(data.quoteType === QuoteTypeEnum.IncomeProtection || data.quoteType === QuoteTypeEnum.ExecutiveIncomeProtection)
                                                                && regularQuote.occupationalClass != null ? (
                                                                <Typography variant="body2" textAlign="center" color="text.secondary" mt={0.5}>
                                                                    Occupational class {regularQuote.occupationalClass} assumed
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                        {data.quoteType === QuoteTypeEnum.IncomeProtection ?
                                                            !!data.salary && data.salary > 44000 ?
                                                                <Box display="flex" justifyContent="center" sx={useBoxStyles.taxReliefBox}>
                                                                    <Typography variant="body1" fontWeight="bold" textAlign="center">
                                                                        {formatQuotePrice(regularQuote.providerQuote - regularQuote.providerQuote * 0.4, false)} after tax relief of 40%
                                                                    </Typography>
                                                                </Box> :
                                                                <Box display="flex" justifyContent="center" sx={useBoxStyles.taxReliefBox}>
                                                                    <Typography variant="body1" fontWeight="bold" textAlign="center">
                                                                        {formatQuotePrice(regularQuote.providerQuote - regularQuote.providerQuote * 0.2, false)} after tax relief of 20%
                                                                    </Typography>
                                                                </Box> : <></>
                                                        }
                                                    </Box>

                                                    <Box display="flex" justifyContent={{ xs: "start", md: "center" }}
                                                        marginBottom={regularQuote.providerBenefits && regularQuote.providerBenefits.length > 0 ? 2 : "auto"}>
                                                        <Typography variant="h4" marginTop="10px">
                                                            {quoteDescription}
                                                        </Typography>
                                                    </Box>

                                                    {/*{!!regularQuote.providerBenefits && regularQuote.providerBenefits.length > 0 &&*/}
                                                    {/*    <>*/}
                                                    {/*        <Box display="flex" justifyContent={{ xs: "start", md: "center" }}>*/}
                                                    {/*            <Typography fontSize={{ xs: "12px", md: "22px" }} marginTop="10px">*/}
                                                    {/*                <Typography variant="h5" fontWeight="bold" textAlign="center" color="navy">Benefits:</Typography>*/}
                                                    {/*                {regularQuote.providerBenefits.map((item, index) => (*/}

                                                    {/*                    <div key={index} style={{ display: "flex", justifyContent: "start", alignItems: "center", marginTop: "8px" }}>*/}
                                                    {/*                        <CheckCircle*/}
                                                    {/*                            sx={(theme) => ({ marginRight: "2px", color: theme.palette.pulse.main, fontSize: { xs: "20px", md: "24px" } })}*/}
                                                    {/*                        />*/}

                                                    {/*                        <span style={{ marginLeft: "4px" }}><Typography variant="h5">{item.description}</Typography></span>*/}
                                                    {/*                    </div>*/}
                                                    {/*                ))}*/}
                                                    {/*            </Typography>*/}
                                                    {/*        </Box>*/}
                                                    {/*    </>*/}

                                                    {/*}*/}

                                                    {!!regularQuote.providerBenefits?.length && (
                                                        <Box
                                                            display="flex"
                                                            flexDirection="column"
                                                            alignItems={{ xs: "flex-start", md: "center" }}
                                                            mt={2}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                fontWeight="bold"
                                                                textAlign={{ xs: "left", md: "center" }}
                                                                color="navy"
                                                                mb={1}
                                                            >
                                                                Benefits:
                                                            </Typography>

                                                            <Box>
                                                                {regularQuote.providerBenefits.map((item, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        display="flex"
                                                                        alignItems="flex-start"
                                                                        mt={1}
                                                                    >
                                                                        <CheckCircle
                                                                            sx={(theme) => ({
                                                                                mr: 1,
                                                                                mt: { xs: "2px", md: "6px" },
                                                                                color: theme.palette.pulse.main,
                                                                                fontSize: { xs: 20, md: 24 },
                                                                            })}
                                                                        />

                                                                        <Typography sx={{ fontSize: { xs: "16px", md: "22px" } }}>
                                                                            {item.description}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    <Box sx={useBoxStyles.buttonsBox} gap={{ xs: 2, md: 4 }}>
                                                        {regularQuote.isApplicable && (
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                sx={{ width: "100% !important", padding: "20px !important" }}
                                                                onClick={() =>
                                                                    createNewFormForClient(
                                                                        FormTemplateTypeEnum.HealthQuestionnaire,
                                                                        regularQuote.providerId,
                                                                        regularQuote.quoteType
                                                                    )
                                                                }
                                                            >
                                                                <Typography fontWeight="bold" sx={{ color: "inherit" }}>
                                                                    APPLY NOW
                                                                </Typography>
                                                            </Button>
                                                        )}

                                                        <Button
                                                            variant="soft"
                                                            color="secondary"
                                                            sx={{ width: "100% !important", padding: "20px !important" }}
                                                            onClick={() =>
                                                                window.open(
                                                                    getBrandCallbackLink(params.brand),
                                                                    '_blank',
                                                                    'noopener,noreferrer'
                                                                )
                                                            }
                                                        >
                                                            <Typography fontWeight="bold" sx={{ color: "inherit" }}>
                                                                REQUEST A CALL BACK
                                                            </Typography>
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            sx={{ width: "100% !important", padding: "20px !important" }}
                                                            onClick={() => toggleExpandCards(regularQuote.providerId)}
                                                        >
                                                            <Typography fontWeight="bold" sx={{ color: "inherit" }}>
                                                                EXPLORE UPGRADE OPTIONS
                                                            </Typography>
                                                        </Button>


                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Grid>


                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                height: '100%',
                                                gap: 2,
                                                backgroundColor: "#f2f2f2",
                                                //boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)"
                                            }}
                                        >
                                            <Grid item xs={12}>
                                                <QuoteSection
                                                    title={QuoteLevelEnum.Bronze}
                                                    quote={bronze}
                                                    healthApplicationData={healthApplicationData}
                                                    regularQuote={regularQuote.providerQuote}
                                                    isExpanded={expandedProviderId === regularQuote.providerId}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <QuoteSection
                                                    title={QuoteLevelEnum.Silver}
                                                    quote={silver}
                                                    healthApplicationData={healthApplicationData}
                                                    regularQuote={regularQuote.providerQuote}
                                                    isExpanded={expandedProviderId === regularQuote.providerId}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <QuoteSection
                                                    title={QuoteLevelEnum.Gold}
                                                    quote={gold}
                                                    healthApplicationData={healthApplicationData}
                                                    regularQuote={regularQuote.providerQuote}
                                                    isExpanded={expandedProviderId === regularQuote.providerId}
                                                />
                                            </Grid>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </Box>
                        </Container>

                        <Divider sx={{ margin: '0 auto', marginBottom: 4, marginTop: 4, border: "1px solid grey", width: '55%' }} />
                    </>
                )
            })}

        </>
    );

};

